import { useEffect, useState } from 'react';

interface Schedule {
    start: string;
    end: string;
}

interface Therapist {
    id: number;
    name: string;
    slug: string;
    avatar_url: string;
    profile_url: string;
    intro_text: string;
    is_working_today: boolean;
    schedule: Schedule | null;
}

interface ApiResponse {
    therapists: Therapist[];
}

export default function TherapistList({ lang = 'EN' }: { lang?: string }) {
    const [therapists, setTherapists] = useState<Therapist[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setLoading(true);
        fetch(`/api-therapists.php?lang=${lang}`)
            .then(res => {
                if (!res.ok) throw new Error('Failed to fetch data');
                return res.json();
            })
            .then((data: ApiResponse) => {
                setTherapists(data.therapists || []);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setError('Unable to load therapists at this time.');
                setLoading(false);
            });
    }, [lang]);

    if (loading) {
        return <div className="text-center py-10 text-gray-500">Loading therapists...</div>;
    }

    if (error) {
        return <div className="text-center py-10 text-red-400">{error}</div>;
    }

    if (therapists.length === 0) {
        return <div className="text-center py-10 text-gray-500">No therapists found.</div>;
    }

    return (
        <div className="flex gap-5 overflow-x-auto pb-6 scrollbar-hide snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {therapists.map((therapist) => (
                <div key={therapist.id} className="flex-shrink-0 w-72 snap-start">
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow relative h-full flex flex-col">

                        <div className="h-80 w-full overflow-hidden bg-gray-100 relative">
                            {therapist.is_working_today && (
                                <div className="absolute top-3 left-3 bg-green-500 text-white text-xs px-3 py-1 rounded-full font-semibold shadow-lg z-10 flex items-center gap-1">
                                    <span>✦ Working Today</span>
                                    {therapist.schedule && (
                                        <span className="opacity-80">{therapist.schedule.start}〜{therapist.schedule.end}</span>
                                    )}
                                </div>
                            )}
                            <img
                                src={therapist.avatar_url || 'https://via.placeholder.com/300x400?text=No+Image'}
                                alt={therapist.name}
                                className="w-full h-full object-cover object-top"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x400?text=No+Image';
                                }}
                            />
                        </div>

                        <div className="p-5 bg-white flex-1 flex flex-col">
                            <h3 className="text-xl font-bold text-gray-800">{therapist.name}</h3>

                            <div className="mt-2 text-sm text-gray-600 flex-1 overflow-y-auto max-h-40 scrollbar-hide">
                                {therapist.intro_text || 'Premium therapist at Sakura-an.'}
                            </div>

                            <a
                                href={therapist.profile_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[#FFB7C5] mt-4 flex items-center gap-1 hover:gap-2 transition-all cursor-pointer whitespace-nowrap"
                            >
                                View Profile <i className="ri-arrow-right-line"></i>
                            </a>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}
